import { ListNode, Tree } from "components/objectTree/types";
import { ObjectVisibility } from "features/render/types";
import { SelectionSet } from "utils/selectionSet";
import { getGroupStateForIdSet, GroupState } from "utils/treeHelpers";

import { ModelTreeNodeData } from "./types";

/**
 * Subtractive: everything is visible by default and the eye toggles the Hidden set.
 * Additive: everything is faded/removed by default and the eye toggles the Shown set
 * (unioned with the selection basket for read purposes).
 */
export type ModelTreeVisibilityMode = "subtractive" | "additive";

export function getModelTreeVisibilityMode(
    defaultVisibility: ObjectVisibility,
    isProcoreDesign: boolean,
): ModelTreeVisibilityMode {
    // Procore mounts this tree with its own row renderer and keeps its keep-set in
    // TransparentSelectionBase, so the Shown context is never populated there and an additive
    // mode would have nothing to read.
    if (isProcoreDesign) {
        return "subtractive";
    }

    switch (defaultVisibility) {
        case ObjectVisibility.SemiTransparent:
        case ObjectVisibility.Transparent:
            return "additive";
        case ObjectVisibility.Neutral:
            return "subtractive";
        case ObjectVisibility.Xray:
            // Only Procore's Isolate enters X-ray, and that is answered above.
            throw new Error("Model tree asked for a visibility mode in X-ray outside Procore");
        default: {
            const _exhaustive: never = defaultVisibility;
            return _exhaustive;
        }
    }
}

/**
 * How much of `item` is visible. Only additive mode tracks visibility directly.
 */
export function getNodeVisibilityState({
    mode,
    hidden,
    shown,
    basket,
    item,
    tree,
}: {
    mode: ModelTreeVisibilityMode;
    hidden: SelectionSet;
    shown: SelectionSet;
    basket: SelectionSet;
    item: ListNode<ModelTreeNodeData>;
    tree: Tree<ModelTreeNodeData>;
}): GroupState {
    if (mode === "additive") {
        return getGroupStateForIdSet(getRevealedIdSet(shown, basket), item, tree);
    }

    return invert(getGroupStateForIdSet(hidden, item, tree));
}

/** Whether a single object (ignoring descendants) renders at natural appearance. */
export function isObjectVisible({
    mode,
    hidden,
    shown,
    basket,
    id,
}: {
    mode: ModelTreeVisibilityMode;
    hidden: SelectionSet;
    shown: SelectionSet;
    basket: SelectionSet;
    id: number;
}): boolean {
    return mode === "additive" ? getRevealedIdSet(shown, basket).has(id) : !hidden.has(id);
}

function invert(state: GroupState): GroupState {
    switch (state) {
        case "all":
            return "none";
        case "none":
            return "all";
        case "some":
            return "some";
        default: {
            const _exhaustive: never = state;
            return _exhaustive;
        }
    }
}

// The tree count caches in treeHelpers are WeakMap-keyed on the set identity, so the same
// inputs have to yield the same set — otherwise every render would miss the cache and
// re-traverse the subtree.
let lastRevealed: { shown: SelectionSet; basket: SelectionSet; result: SelectionSet } | undefined;

/** The objects rendered at natural appearance in additive mode: Shown plus the selection basket. */
export function getRevealedIdSet(shown: SelectionSet, basket: SelectionSet): SelectionSet {
    if (lastRevealed?.shown === shown && lastRevealed?.basket === basket) {
        return lastRevealed.result;
    }

    const result = shown.withAdded(basket.sortedIds);
    lastRevealed = { shown, basket, result };
    return result;
}
